import { type VersionInfo, VersionInfoSchema } from '@innermate/shared';
import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';

// 새 배포 감지 폴링 + 현재 빌드 정보 노출. 단일 진실 소스 1군데에서 polling 하고
// NewVersionToast / UserChip 양쪽이 같은 값을 본다.

const POLL_INTERVAL_MS = 60_000;

interface BuildVersionState {
  /** 페이지 진입 시점에 잡은 식별자. 비교 기준. */
  initial: VersionInfo | null;
  /** 마지막 polling 결과. 항상 최신. UserChip 표시에 사용. */
  current: VersionInfo | null;
  /** initial 과 current 가 다르면 true. 토스트 트리거. */
  newVersionAvailable: boolean;
}

const BuildVersionContext = createContext<BuildVersionState>({
  initial: null,
  current: null,
  newVersionAvailable: false,
});

export function useBuildVersion(): BuildVersionState {
  return useContext(BuildVersionContext);
}

/**
 * 두 식별자를 비교해 "다른 배포" 인지 판정.
 * builtAt 이 있으면 그게 1순위 (매 빌드 자동 변경). 없으면 commit. 그것도 같으면 version.
 * dev 모드 응답 (builtAt=null, commit='dev') 끼리 비교하면 같음 → 토스트 안 뜸.
 */
function isSameBuild(a: VersionInfo, b: VersionInfo): boolean {
  if (a.builtAt || b.builtAt) return a.builtAt === b.builtAt;
  if (a.commit !== b.commit) return false;
  return a.version === b.version;
}

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const res = await fetch('/api/version', { credentials: 'include' });
    if (!res.ok) return null;
    const json = await res.json();
    const parsed = VersionInfoSchema.safeParse(json);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function BuildVersionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BuildVersionState>({
    initial: null,
    current: null,
    newVersionAvailable: false,
  });
  const initialRef = useRef<VersionInfo | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const info = await fetchVersion();
      if (cancelled || !info) return;
      setState((prev) => {
        const initial = initialRef.current ?? info;
        if (!initialRef.current) initialRef.current = info;
        const newVersionAvailable = !isSameBuild(initial, info);
        // 이미 stale 로 잡혔다면 그 상태 유지 (서버가 잠시 옛 워커로 응답해도 토스트가 깜빡이지 않게).
        return {
          initial,
          current: info,
          newVersionAvailable: prev.newVersionAvailable || newVersionAvailable,
        };
      });
    };

    void check();

    // dev (version 'dev') 는 polling 자체를 건너뛴다 — vite HMR 이 알아서 갱신.
    // 단 최초 1회 fetch 는 UserChip 에 'dev' 표시를 위해 수행.
    const skipPolling = initialRef.current?.version === 'dev';
    const timer = skipPolling ? null : setInterval(check, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <BuildVersionContext.Provider value={state}>{children}</BuildVersionContext.Provider>;
}
